import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import { Button } from './ui/Button';
import { PageLoader } from './ui/PageLoader';
import { AdminQueueState } from './ui/AdminQueueState';
import { AdminDeleteButton } from './ui/AdminDeleteButton';
import { useToast } from '../contexts/ToastContext';
import { useAdminTabParam } from '../hooks/useAdminTabParam';

const CLUB_TABS = ['queue', 'directory'] as const;
const STATUS_OPTIONS = ['pending_review', 'unverified', 'verified', 'suspended', 'draft'] as const;

const TAB_LABELS: Record<(typeof CLUB_TABS)[number], string> = {
  queue: 'Review queue',
  directory: 'Directory',
};

const AdminClubsReview: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useAdminTabParam(CLUB_TABS, 'queue');
  const [status, setStatus] = useState<string>('pending_review');
  const [clubs, setClubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const effectiveStatus = tab === 'queue' ? 'pending_review' : status;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.adminListClubs(effectiveStatus);
      setClubs((res as any).data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab === 'queue') {
      setStatus('pending_review');
    }
  }, [tab]);

  const statusLabel = effectiveStatus.replace(/_/g, ' ');

  const review = async (clubId: number, action: 'approve' | 'verify' | 'suspend' | 'reject') => {
    setBusyId(clubId);
    try {
      await apiService.adminReviewClub(clubId, action);
      showToast(
        `Club ${
          action === 'approve'
            ? 'approved'
            : action === 'verify'
              ? 'verified'
              : action === 'suspend'
                ? 'suspended'
                : 'rejected'
        }.`,
        'success'
      );
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : `Failed to ${action} club`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="text-white">
      <PageHeader
        eyebrow="Admin"
        title="Clubs"
        description="Review club submissions and browse the club directory."
        backLink={{ to: '/admin', label: 'Overview' }}
      />

      <div role="tablist" aria-label="Club moderation views" className="flex flex-wrap gap-1 mb-4">
        {CLUB_TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`px-3 py-2 rounded-lg text-sm font-medium min-h-[40px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
              tab === key
                ? 'bg-orange-500/[0.12] text-orange-300 border border-orange-500/30'
                : 'text-gray-400 border border-transparent hover:bg-white/[0.04] hover:text-white'
            }`}
          >
            {TAB_LABELS[key]}
          </button>
        ))}
      </div>

      {tab === 'directory' ? (
        <div className="flex flex-wrap gap-2 mb-4" aria-label="Directory status filter">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              className={status === s ? 'filter-chip-active' : 'filter-chip'}
              onClick={() => setStatus(s)}
            >
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? <PageLoader message="Loading clubs" /> : null}
      {!loading && error ? (
        <AdminQueueState
          variant="error"
          title="Could not load clubs"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}
      {!loading && !error && clubs.length === 0 ? (
        <AdminQueueState
          variant={tab === 'queue' ? 'empty' : 'filter-empty'}
          title={tab === 'queue' ? 'No clubs waiting for review' : `No clubs with status “${statusLabel}”`}
          description={
            tab === 'queue'
              ? 'New club submissions will appear here for approval.'
              : 'Try another status filter to browse the directory.'
          }
          onClearFilter={tab === 'directory' && status !== 'pending_review' ? () => setStatus('pending_review') : undefined}
          clearFilterLabel="Show pending review"
        />
      ) : null}
      {!loading && !error && clubs.length > 0 ? (
        <ul className="space-y-3">
          {clubs.map((c) => (
            <li
              key={c.id}
              className="rounded-xl border border-white/[0.06] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
            >
              <div>
                <Link to={`/clubs/${c.slug}`} className="text-orange-400 hover:text-orange-300 font-medium">
                  {c.name}
                </Link>
                <p className="text-sm text-gray-500 mt-1">
                  {c.region} · {c.verification_status}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" disabled={busyId === c.id} onClick={() => void review(c.id, 'approve')}>
                  Approve
                </Button>
                <Button variant="primary" disabled={busyId === c.id} onClick={() => void review(c.id, 'verify')}>
                  Verify
                </Button>
                <Button variant="ghost" disabled={busyId === c.id} onClick={() => void review(c.id, 'suspend')}>
                  Suspend
                </Button>
                <Button variant="danger" disabled={busyId === c.id} onClick={() => void review(c.id, 'reject')}>
                  Reject
                </Button>
                <AdminDeleteButton
                  entity="club"
                  id={c.id}
                  confirmToken={c.slug}
                  label={c.name}
                  description={
                    <>
                      Permanently deletes club <strong className="text-white">{c.name}</strong>, memberships,
                      vehicle links, posts, and club badges. Member accounts, vehicles, and linked events stay.
                    </>
                  }
                  onDeleted={() => void load()}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default AdminClubsReview;
